import { useLingui } from '@lingui/react/macro';
import { Link, useNavigate, useSearch, isNotFound, useRouteContext } from '@tanstack/react-router';
import { FilterYearIcon } from './assets/filter-year-icon';
import { categoriesKeys } from '@worldcoo/shared/categories';
import { categoryMessages } from '@/lib/portal-messages';
import { FilterCategoryIcon } from './assets/filter-category-icon';
import { Separator } from '@worldcoo/ui/components/separator';
import { Button } from '@worldcoo/ui/components/button';
import { CheckIcon, XIcon, ZapIcon } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { Suspense } from 'react';
import { usePortalCampaignsQuery } from '@/lib/data-hooks';
import { useFilterPanel } from './filter-panel';
import { useIsFilterApplied } from '@/lib/use-filter-applied';

export function CampaignsFiltersSidebar() {
	return (
		<Suspense>
			<SuspensedCampaignsFiltersSidebar />
		</Suspense>
	);
}

function SuspensedCampaignsFiltersSidebar() {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/$portalId/' });
	const filterPanel = useFilterPanel();
	const isFilterApplied = useIsFilterApplied();

	const handleClose = () => {
		filterPanel.setOpen(false);
	};

	const clearFilters = () => {
		navigate({ search: { year: undefined, causes: undefined }, resetScroll: false });
		handleClose();
	};

	return (
		<div className="bg-background flex h-full flex-col">
			<div className="flex items-center justify-between border-b px-4 py-3">
				<h1 className="px-2 py-1 text-2xl">{t`Filter`}</h1>
				<Button type="button" variant="ghost" size="icon" onClick={handleClose}>
					<XIcon className="size-7" strokeWidth={1} />
					<span className="sr-only">{t`Close`}</span>
				</Button>
			</div>

			<DivWithPrimaryColorVariable className="scrollbar flex flex-1 flex-col gap-6 overflow-y-scroll px-3 py-6 lg:flex-none">
				<div className="flex flex-col gap-3">
					<div className="flex gap-3">
						<FilterYearIcon className="text-primary size-6" />
						{t`Year`}
					</div>
					<YearFilter className="flex-row flex-wrap p-0" />
				</div>

				<Separator />

				<div className="flex flex-col gap-3">
					<div className="flex gap-3">
						<FilterCategoryIcon className="text-primary size-6" />
						{t`Cause`}
					</div>

					<CausesFilter className="p-0" />
				</div>
			</DivWithPrimaryColorVariable>

			{isFilterApplied && (
				<div className="border-t p-0">
					<DivWithPrimaryColorVariable className="flex gap-3 p-3">
						<Button type="button" className="w-full shrink" onClick={clearFilters}>
							{t`Clear Filters`}
						</Button>
					</DivWithPrimaryColorVariable>
				</div>
			)}
		</div>
	);
}

// MARK: Year Filter
function YearFilter(props: { className?: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { year } = useSearch({ from: '/$portalId/' });
	const campaigns = usePortalCampaignsQuery();

	if (isNotFound(campaigns)) return null;

	return (
		<ul className={cn('flex w-72 flex-wrap gap-2 p-3', props.className)}>
			{campaigns.yearOptions.map((yearOption) => (
				<li key={yearOption} className="contents">
					<Link
						to="/$portalId"
						params={{ portalId }}
						search={(prev) => ({ ...prev, year: yearOption === year ? undefined : yearOption })}
						resetScroll={false}
						className="aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground flex h-10 flex-auto grow-0 items-center justify-center rounded-sm border px-2.5 text-sm"
					>
						{yearOption}
					</Link>
				</li>
			))}
		</ul>
	);
}

// MARK: State Filter
export function StateFilter(props: { className?: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const { state } = useSearch({ from: '/$portalId/' });
	const campaigns = usePortalCampaignsQuery();

	if (isNotFound(campaigns)) return null;

	const { campaignsCount, activeCampaignsCount, filteredCampaignsCount, filteredActiveCampaignsCount } = campaigns;
	const finishedCampaignsCount = campaignsCount - activeCampaignsCount;
	const finishedFilteredCampaignsCout = filteredCampaignsCount - filteredActiveCampaignsCount;

	if (!portalId) return;
	if (activeCampaignsCount === 0 || finishedCampaignsCount === 0) return;

	return (
		<ul className={cn('bg-background shadow-3xl flex gap-2 rounded-xl p-1 lg:w-auto', props.className)}>
			<li className="contents">
				<Link
					to="/$portalId"
					params={{ portalId }}
					search={(prev) => ({ ...prev, state: undefined })}
					activeOptions={{ exact: true }}
					resetScroll={false}
					className="aria-[current=page]:bg-primary/10 flex w-full flex-row items-center justify-center gap-3 rounded-lg p-2 text-nowrap transition-colors"
				>
					<ZapIcon
						className={cn(
							'bg-primary/20 text-primary size-5 shrink-0 rounded-full p-1',
							!state && 'text-primary-foreground bg-primary',
						)}
					/>
					{t`Active`} ({filteredActiveCampaignsCount})
				</Link>
			</li>

			<li className="contents">
				<Link
					to="/$portalId"
					params={{ portalId }}
					search={(prev) => ({ ...prev, state: 'finished' })}
					resetScroll={false}
					className="aria-[current=page]:bg-primary/10 flex w-full flex-row items-center justify-center gap-3 rounded-lg p-2 text-nowrap transition-colors"
				>
					<CheckIcon
						className={cn(
							'bg-primary/20 text-primary size-5 shrink-0 rounded-full p-1',
							state === 'finished' && 'bg-primary text-primary-foreground',
						)}
						strokeWidth={4}
					/>
					{t`Finished`} ({finishedFilteredCampaignsCout})
				</Link>
			</li>
		</ul>
	);
}

// MARK: Causes Filter
function CausesFilter(props: { className?: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const { causes } = useSearch({ from: '/$portalId/' });

	if (!portalId) return;

	return (
		<ul className={cn('flex w-72 flex-wrap gap-2 p-3', props.className)}>
			{categoriesKeys.map((cause) => (
				<li key={cause} className="contents">
					<Link
						to="/$portalId"
						params={{ portalId }}
						search={(prev) => ({
							...prev,
							causes: causes?.includes(cause) ? causes.filter((c) => c !== cause) : [...(causes ?? []), cause],
						})}
						aria-current={causes?.includes(cause) ? 'page' : undefined}
						resetScroll={false}
						className="aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground flex h-10 flex-auto grow-0 items-center rounded-sm border px-2.5 text-sm"
					>
						{categoryMessages[cause] ? t(categoryMessages[cause]) : cause}
					</Link>
				</li>
			))}
		</ul>
	);
}
